import React, { useState, useEffect, useMemo } from "react";
import { Helmet } from "react-helmet";
import { makeStyles } from "@material-ui/core/styles";
import { useDispatch, useSelector } from "react-redux";
import { hasCap, isSuper, hasMoney } from "../../lib/permissions";
import Sidebar from "../../components/sidebar";
import Overview from "../../components/overview";
import Transactions from "../../components/transaction";
import Companies from "../../components/companies";
import Merchants from "../../components/merchants";
import Exchanges from "../../components/exchanges";
import AdminReports from "../../components/adminReports";
import AdminTransactions from "../../components/adminTransactions";
import SanaamPayment from "../../components/sanaamPayments";
import Deductions from "../../components/deductions";
import WithdrawLimitCategory from "../../components/withdrawaLimit/category";
import { ThreeDotsLoading } from "../../components/loading";
import Countries from "../../components/countries";
import GeneralStaffOverview from "../../components/generalStaffOverview";
import PushNotification from "../../components/pushNotifications";
import ReviewSupportContainer from "../../components/hoverAll/supportRequestContainer";
import NotificationsReports from "../../components/notificationsReports";
import UserActivityLogsPage from "../../components/userActivityLogs";
import EmployeeValidator from "../../components/employees/employeeValidator";
import FeatureFlagsPage from "../../components/featureFlags";
import EngagementPage from "../../components/engagement";
import BonusesAdminPage from "../../components/bonusesAdmin";
import CampaignEnginePage from "../../components/campaignEngine";
import RoleGroupsPage from "../../components/roleGroups";
import FortressGatewayPage from "../../components/fortressGateway";
import { fetchFeatureFlagsRequest } from "../featureFlags/actions";

const useStyles = makeStyles(() => ({ root: { display: "flex" } }));

function Dashboard({ match, location }) {
  const classes = useStyles();
  const dispatch = useDispatch();
  const [activeTab, setActiveTab] = useState("overview");

  const dashboardReducer = useSelector((state) => {
    return state.dashboardReducer;
  });

  const companyDashboardReducer = useSelector((state) => {
    return state.companyDashboardReducer;
  });
  const featureFlags = useSelector(
    (state) => state?.featureFlagsReducer?.items || []
  );

  const featureFlagUpdate = useMemo(
    () => featureFlags.find((item) => item?.name === "feature_flag_update"),
    [featureFlags]
  );
  const showFeatureFlagsTab = featureFlagUpdate
    ? !!featureFlagUpdate?.is_enabled
    : true;

  // Current user (prefer the freshest /auth/me result, fall back to the stored
  // login user) — used to permission-gate tabs via their `show` condition.
  const me = useSelector((state) => state?.loginReducer?.me);
  const currentUser = useMemo(() => {
    if (me) return me;
    try {
      return JSON.parse(localStorage.getItem("user"));
    } catch (e) {
      return null;
    }
  }, [me]);

  const dashboardTabs = useMemo(
    () => [
      {
        name: "Overview",
        icon: "Overview Icon.svg",
        isActive: true,
        pathName: "overview",
        show: () => hasCap(currentUser, "overview.view"),
      },
      {
        name: "Transactions",
        icon: "Transaction Icon.svg",
        isActive: true,
        pathName: "transactions",
        // Money-centric page — also requires financials.view.
        show: () => hasCap(currentUser, "transactions.view") && hasMoney(currentUser),
      },
      {
        name: "Companies",
        icon: "Company Icon.svg",
        isActive: true,
        pathName: "companies",
        show: () => hasCap(currentUser, "companies.view"),
      },
      {
        name: "Role Groups",
        icon: "staff_svg.svg",
        isActive: true,
        pathName: "role-groups",
        // Role Groups management is SUPER-ONLY (no capability exists).
        show: () => isSuper(currentUser),
      },
      {
        name: "Merchants",
        icon: "Company Icon.svg",
        isActive: true,
        pathName: "merchants",
        show: () => hasCap(currentUser, "vendors.view"),
      },
      {
        name: "Reports",
        icon: "Report Icon.svg",
        isActive: false,
        comingSoon: true,
        pathName: "admin-reports",
        show: () => hasCap(currentUser, "reports.view"),
      },
      {
        name: "Sanaam Payments",
        icon: "Sanaam Payments.svg",
        isActive: true,
        comingSoon: false,
        pathName: "SanaamPayments",
        // Money-centric page — also requires financials.view.
        show: () => hasCap(currentUser, "company_payments.view") && hasMoney(currentUser),
      },
      {
        name: "Transistory",
        icon: "AdminTransactionIcon.svg",
        isActive: true,
        pathName: "admin-transactions",
        // Money-centric page — also requires financials.view.
        show: () => hasCap(currentUser, "payments.view") && hasMoney(currentUser),
      },
      {
        name: "User Activity Logs",
        icon: "activity.svg",
        isActive: true,
        comingSoon: false,
        pathName: "UserActivityLogs",
        show: () => hasCap(currentUser, "logs.view"),
      },
      {
        name: "General Staff Overview",
        isActive: true,
        pathName: "generalStaffOverview",
        icon: "staff_svg.svg",
        show: () => hasCap(currentUser, "employees.view"),
      },
      {
        name: "Employee Validator",
        icon: "Employees_Icon.svg",
        isActive: true,
        pathName: "employeeValidator",
        show: () => hasCap(currentUser, "employee_upload.view"),
      },
      {
        // Grouped section. The pathName here is the group's stable id (the
        // sidebar keys expanded state on it) — it isn't a route. Children
        // carry the real routes.
        name: "Campaign Engine",
        icon: "Report Icon.svg",
        isActive: true,
        pathName: "campaign-engine-group",
        children: [
          {
            name: "Engine",
            icon: "Report Icon.svg",
            isActive: true,
            pathName: "campaign-engine",
            show: () => hasCap(currentUser, "campaigns.view"),
          },
          {
            name: "Bonuses",
            icon: "Sanaam Payments.svg",
            isActive: true,
            pathName: "bonuses",
            show: () => hasCap(currentUser, "bonuses.view"),
          },
          {
            name: "Engagement",
            icon: "activity.svg",
            isActive: true,
            pathName: "engagement",
            show: () => hasCap(currentUser, "engagement.view"),
          },
        ],
      },
      {
        // Grouped config section. `pathName` is the group's stable id (for the
        // sidebar's expanded state), not a route — children carry the routes.
        // The group auto-hides when none of its children are visible.
        name: "Configuration",
        icon: "setting.png",
        isActive: true,
        pathName: "configuration-group",
        children: [
          {
            name: "Payment Gateway",
            icon: "setting.png",
            isActive: true,
            pathName: "payment-gateway",
            // Fortress gateway config is SUPER-ONLY (no capability exists).
            show: () => isSuper(currentUser),
          },
          {
            name: "Feature Flags",
            icon: "Report Icon.svg",
            isActive: true,
            pathName: "featureFlags",
            show: () =>
              showFeatureFlagsTab && hasCap(currentUser, "feature_flags.view"),
          },
        ],
      },
    ],
    [showFeatureFlagsTab, currentUser]
  );

  useEffect(() => {
    dispatch(fetchFeatureFlagsRequest());
  }, [dispatch]);

  useEffect(() => {
    if (match?.params?.activeTab && match.params.activeTab !== activeTab) {
      setActiveTab(match.params.activeTab);
    }
  }, [match, activeTab, setActiveTab]);

  useEffect(() => {
    if (!showFeatureFlagsTab && activeTab === "featureFlags") {
      setActiveTab("overview");
    }
  }, [showFeatureFlagsTab, activeTab]);

  return (
    <div className={classes.root}>
      <Helmet>
        <title>{`Sanaam - ${activeTab}`}</title>
        <meta property="og:title" content={`Sanaam - ${activeTab}`} />
      </Helmet>
      <ThreeDotsLoading
        showLoader={
          dashboardReducer?.loading || companyDashboardReducer?.loading
        }
      />
      <Sidebar
        baseURL={"/backoffice/dashboard"}
        activeTab={activeTab}
        tabs={dashboardTabs}
      />
      {activeTab === "overview" && <Overview />}
      {activeTab === "transactions" && hasMoney(currentUser) && <Transactions />}
      {activeTab === "companies" && <Companies location={location} />}
      {activeTab === "merchants" && <Merchants location={location} />}
      {activeTab === "Exchanges" && <Exchanges />}
      {activeTab === "WithdrawalLimits" && <WithdrawLimitCategory />}
      {activeTab === "admin-reports" && <AdminReports />}
      {activeTab === "admin-transactions" && hasMoney(currentUser) && <AdminTransactions />}
      {/* {activeTab === "Deductions" && <Deductions />} */}
      {activeTab === "Countries" && <Countries />}
      {activeTab === "SanaamPayments" && hasMoney(currentUser) && <SanaamPayment />}
      {activeTab === "generalStaffOverview" && <GeneralStaffOverview />}
      {activeTab === "pushNotifications" && hasCap(currentUser, "notifications.broadcast") && <PushNotification />}
      {activeTab === "notificationsReports" && hasCap(currentUser, "reports.send") && <NotificationsReports />}
      {activeTab === "UserActivityLogs" && <UserActivityLogsPage />}
      {activeTab === "employeeValidator" && <EmployeeValidator />}
      {activeTab === "featureFlags" && showFeatureFlagsTab && <FeatureFlagsPage />}
      {activeTab === "engagement" && hasCap(currentUser, "engagement.view") && <EngagementPage />}
      {activeTab === "bonuses" && <BonusesAdminPage />}
      {activeTab === "campaign-engine" && <CampaignEnginePage />}
      {activeTab === "role-groups" && isSuper(currentUser) && <RoleGroupsPage />}
      {activeTab === "payment-gateway" && isSuper(currentUser) && <FortressGatewayPage />}
      {/* <SupportButton /> */}
      <ReviewSupportContainer />
    </div>
  );
}

export default Dashboard;
